import { useEffect, useState, type FormEvent } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { api } from '../api/client';
import type { ApiTokenDto } from '../api/generated/client';
import { Box, Stack, Typography } from './mui-system';

const statuses = { active: 'Активен', expired: 'Истёк', revoked: 'Отозван' };

export function ApiTokensCard() {
  const [tokens, setTokens] = useState<ApiTokenDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [listError, setListError] = useState('');
  const [name, setName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [created, setCreated] = useState<{ secret: string; name: string } | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiTokenDto | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState('');

  useEffect(() => {
    let active = true;
    api
      .apiTokens()
      .then((rows) => {
        if (active) setTokens(rows);
      })
      .catch((caught) => {
        if (active) setListError(message(caught));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError('');
    try {
      const result = await api.createApiToken({ name: name.trim(), expiresInDays });
      setTokens((previous) => [result.token, ...previous]);
      setCreated({ secret: result.secret, name: result.token.name });
      setName('');
      setCopied(false);
      setCopyError('');
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (!revokeTarget) return;
    setBusy(true);
    setError('');
    try {
      const result = await api.revokeApiToken(revokeTarget.id);
      setTokens((previous) => previous.map((token) => (token.id === result.id ? result : token)));
      setRevokeTarget(null);
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h3">Мои API-токены</Typography>
        <Typography color="text.secondary" fontSize={13} mt={1} mb={2}>
          Для скриптов и интеграций. Токен даёт доступ ко всему API с вашими текущими правами во
          всех доступных вам воркспейсах. Управлять пулом доменов могут редакторы и суперадмины.
        </Typography>
        <Button href="/docs/api-tokens" size="small" sx={{ mb: 2 }}>
          Документация API
        </Button>
        {listError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {listError}. Обновите страницу, чтобы загрузить полный список токенов.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack component="form" onSubmit={create} direction={{ xs: 'column', sm: 'row' }} gap={1}>
          <TextField
            label="Название токена"
            placeholder="Импорт доменов"
            value={name}
            onChange={(event) => setName(event.target.value)}
            slotProps={{ htmlInput: { maxLength: 100 } }}
            disabled={busy}
            fullWidth
          />
          <TextField
            select
            label="Срок действия"
            value={expiresInDays}
            onChange={(event) => setExpiresInDays(Number(event.target.value))}
            disabled={busy}
            sx={{ minWidth: 180 }}
          >
            {[7, 30, 90, 365].map((days) => (
              <MenuItem key={days} value={days}>
                {days} дней
              </MenuItem>
            ))}
          </TextField>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || busy || !name.trim()}
            sx={{ minWidth: 150 }}
          >
            Создать токен
          </Button>
        </Stack>
        {loading ? (
          <CircularProgress aria-label="Загрузка API-токенов" sx={{ mt: 2 }} />
        ) : tokens.length ? (
          <TableContainer sx={{ mt: 2 }}>
            <Table size="small" aria-label="Мои API-токены">
              <TableHead>
                <TableRow>
                  <TableCell>Токен</TableCell>
                  <TableCell>Состояние</TableCell>
                  <TableCell>Создан / действует до</TableCell>
                  <TableCell>Последнее использование</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tokens.map((token) => {
                  const status = token.revokedAt
                    ? 'revoked'
                    : new Date(token.expiresAt) <= new Date()
                      ? 'expired'
                      : token.status;
                  return (
                    <TableRow key={token.id}>
                      <TableCell>
                        <Typography fontWeight={700}>{token.name}</Typography>
                        <Box component="code">{token.prefix}…</Box>
                      </TableCell>
                      <TableCell>{statuses[status]}</TableCell>
                      <TableCell>
                        {date(token.createdAt)}
                        <br />
                        {date(token.expiresAt)}
                      </TableCell>
                      <TableCell>
                        {token.lastUsedAt ? date(token.lastUsedAt) : 'Ещё не использовался'}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          color="error"
                          disabled={busy || status === 'revoked'}
                          aria-label={`Отозвать токен ${token.name}`}
                          onClick={() => setRevokeTarget(token)}
                        >
                          Отозвать
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : !listError ? (
          <Typography color="text.secondary" mt={2}>
            У вас пока нет API-токенов.
          </Typography>
        ) : null}
      </CardContent>
      <Dialog open={Boolean(created)} fullWidth maxWidth="sm">
        <DialogTitle>Сохраните токен «{created?.name}»</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Секрет показан только сейчас. После закрытия его нельзя посмотреть снова.
          </Alert>
          <TextField
            label="Секрет API-токена"
            value={created?.secret ?? ''}
            fullWidth
            multiline
            slotProps={{ input: { readOnly: true } }}
          />
          {copyError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {copyError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(created!.secret);
                setCopied(true);
                setCopyError('');
              } catch {
                setCopyError('Не удалось скопировать. Выделите секрет и скопируйте его вручную.');
              }
            }}
          >
            {copied ? 'Скопировано' : 'Скопировать'}
          </Button>
          <Button variant="contained" onClick={() => setCreated(null)}>
            Я сохранил токен
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(revokeTarget)} onClose={busy ? undefined : () => setRevokeTarget(null)}>
        <DialogTitle>Отозвать токен «{revokeTarget?.name}»?</DialogTitle>
        <DialogContent>
          <Typography>
            Интеграции с этим токеном потеряют доступ. Восстановить токен нельзя.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setRevokeTarget(null)}>
            Отмена
          </Button>
          <Button color="error" disabled={busy} onClick={() => void revoke()}>
            Отозвать навсегда
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

function date(value: string) {
  return new Date(value).toLocaleString('ru');
}
function message(error: unknown) {
  return error instanceof Error ? error.message : 'Не удалось выполнить операцию с API-токеном';
}
